import { computed, onBeforeUnmount, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import { initOnEvents } from 'shared/helpers/AudioNotificationHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { KANBAN_MODULE_PERMISSION_BY_KEY } from 'dashboard/constants/permissions.js';

// Issue #7 — notificação do Chat Interno (som + notificação do navegador).
//
// No sistema antigo isso vinha de um script injetado que ficava olhando o
// contador de não-lidas. Aqui a fonte é o próprio websocket
// (`kanban.internal_chat.message`), que já chega pra todos os membros do chat.
//
// O arquivo é ADITIVO de propósito (padrão do fork): a lógica toda mora aqui e
// só existem dois pontos de encaixe — uma linha no App.vue (pra notificar mesmo
// com o usuário em outra tela do dashboard) e alguns ganchos no KanbanChats.vue
// (chat aberto no momento, toggle e abrir o chat ao clicar na notificação).

// Chave da preferência do usuário. Fica nas uiSettings (persistidas no perfil),
// que é o mecanismo que o projeto já usa pra preferência de UI por usuário.
export const INTERNAL_CHAT_NOTIFICATIONS_KEY =
  'internal_chat_notifications_enabled';

// Mesma chave de som já usada pela tela do Chat Interno — o botão de som de lá
// continua mandando no som daqui também, pro usuário não ter dois controles.
const SOUND_STORAGE_KEY = 'chatSoundEnabled';

const NOTIFICATION_TAG_PREFIX = 'astrachat-internal-chat-';
const NOTIFICATION_AUTO_CLOSE_MS = 8000;
const BODY_MAX_LENGTH = 140;
const DEDUPE_SIZE = 50;

// ===== estado compartilhado entre a tela do módulo e o notificador global =====
// São variáveis de módulo (não refs) porque só são lidas dentro do handler do
// websocket; não precisam disparar renderização.
let mountedViews = 0;
let activeChatId = null;
let pendingChatId = null;
const notifiedMessageIds = [];

/** Chat que está aberto na tela do Chat Interno agora (null = nenhum). */
export const setActiveInternalChatId = id => {
  activeChatId = id === undefined ? null : id;
};

export const getActiveInternalChatId = () => activeChatId;

export const isInternalChatViewMounted = () => mountedViews > 0;

/** Chat que a notificação clicada pediu pra abrir (consumido uma única vez). */
export const consumePendingInternalChatId = () => {
  const id = pendingChatId;
  pendingChatId = null;
  return id;
};

export const isNotificationSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const getNotificationPermission = () =>
  isNotificationSupported() ? window.Notification.permission : 'unsupported';

/**
 * Pede a permissão ao navegador. Só pode ser chamada a partir de um gesto do
 * usuário (clique no toggle ou primeira interação com o módulo) — pedir no load
 * da página é bloqueado pelo Chrome e é chato pro usuário.
 * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>}
 */
export const requestInternalChatNotificationPermission = async () => {
  if (!isNotificationSupported()) return 'unsupported';
  if (window.Notification.permission !== 'default') {
    return window.Notification.permission;
  }
  try {
    // Safari antigo usa a versão com callback e devolve undefined
    const result = window.Notification.requestPermission();
    if (result && typeof result.then === 'function') return await result;
  } catch {
    // navegador sem suporte à chamada — cai no permission atual abaixo
  }
  return window.Notification.permission;
};

/**
 * Registra a tela do Chat Interno como montada. Enquanto ela estiver montada o
 * som fica por conta dela (não duplicamos o beep) e, se a permissão ainda não
 * foi decidida, pedimos na primeira interação do usuário dentro do módulo.
 * @param {Function} isEnabled getter da preferência do usuário
 * @param {Function} onPermission recebe a resposta do navegador ao pedido
 * @returns {Function} função de baixa (chamar no onBeforeUnmount)
 */
export const registerInternalChatView = (
  isEnabled = () => true,
  onPermission = () => {}
) => {
  mountedViews += 1;
  const askOnce = () => {
    document.removeEventListener('click', askOnce, true);
    if (!isEnabled()) return;
    requestInternalChatNotificationPermission().then(onPermission);
  };
  const shouldAsk =
    isNotificationSupported() && window.Notification.permission === 'default';
  if (shouldAsk) document.addEventListener('click', askOnce, true);

  return () => {
    if (shouldAsk) document.removeEventListener('click', askOnce, true);
    mountedViews = Math.max(0, mountedViews - 1);
    if (mountedViews === 0) activeChatId = null;
  };
};

const sameId = (a, b) =>
  a !== undefined && a !== null && String(a) === String(b);

/**
 * Decide se uma mensagem do Chat Interno merece notificação do navegador.
 * Função pura de propósito: é o coração da issue e dá pra testar sem DOM.
 * @returns {boolean}
 */
export const shouldNotifyInternalChat = ({
  enabled = true,
  permission = 'default',
  eventAccountId,
  currentAccountId,
  senderId,
  currentUserId,
  chatId,
  activeChatId: openChatId = null,
  documentHidden = false,
  alreadyNotified = false,
} = {}) => {
  if (!enabled) return false;
  if (permission !== 'granted') return false;
  if (chatId === undefined || chatId === null) return false;
  if (!sameId(eventAccountId, currentAccountId)) return false;
  // nunca notificar as próprias mensagens (o websocket volta pro remetente)
  if (sameId(senderId, currentUserId)) return false;
  // mesma mensagem chegando duas vezes (reconexão do cable, por ex.)
  if (alreadyNotified) return false;
  // usuário já está olhando exatamente esse chat: notificar seria ruído
  if (!documentHidden && sameId(chatId, openChatId)) return false;
  return true;
};

/**
 * Decide se o notificador GLOBAL deve tocar o som. Quando a tela do Chat
 * Interno está montada quem toca é ela (ver KanbanChats.vue / PR #52), então
 * aqui só cobrimos o caso "usuário está em outra tela do dashboard".
 * @returns {boolean}
 */
export const shouldPlayInternalChatSound = ({
  soundEnabled = true,
  viewMounted = false,
  eventAccountId,
  currentAccountId,
  senderId,
  currentUserId,
  chatId,
} = {}) => {
  if (!soundEnabled) return false;
  if (viewMounted) return false;
  if (chatId === undefined || chatId === null) return false;
  if (!sameId(eventAccountId, currentAccountId)) return false;
  if (sameId(senderId, currentUserId)) return false;
  return true;
};

// ===== som =====
// Dois beeps (880Hz + 1100Hz), exatamente o alerta que o cliente já conhecia do
// script antigo. O AudioContext é criado uma vez e destravado no primeiro gesto
// do usuário na página (política de autoplay dos navegadores).
let audioCtx = null;
let audioUnlockBound = false;

const resumeAudioContext = () => {
  try {
    if (!audioCtx || audioCtx.state === 'closed') {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      audioCtx = new Ctx();
    }
    if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  } catch {
    audioCtx = null;
  }
};

const bindAudioUnlock = () => {
  if (audioUnlockBound || typeof document === 'undefined') return;
  audioUnlockBound = true;
  const unlock = () => {
    resumeAudioContext();
    // um gesto basta: depois dele o contexto existe e o navegador libera o som
    initOnEvents.forEach(evt => document.removeEventListener(evt, unlock));
  };
  initOnEvents.forEach(evt =>
    document.addEventListener(evt, unlock, { passive: true })
  );
};

const beep = (frequency, startOffset, duration) => {
  const osc = audioCtx.createOscillator();
  const gain = audioCtx.createGain();
  osc.connect(gain);
  gain.connect(audioCtx.destination);
  osc.type = 'sine';
  osc.frequency.value = frequency;
  const start = audioCtx.currentTime + startOffset;
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(0.3, start + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
  osc.start(start);
  osc.stop(start + duration);
};

export const playInternalChatChime = () => {
  try {
    resumeAudioContext();
    if (!audioCtx || audioCtx.state === 'closed') return;
    beep(880, 0, 0.2);
    beep(1100, 0.15, 0.2);
  } catch {
    // som é enfeite: nunca pode derrubar o handler do websocket
  }
};

// ===== notificação =====
const rememberNotified = messageId => {
  if (messageId === undefined || messageId === null) return;
  notifiedMessageIds.push(String(messageId));
  if (notifiedMessageIds.length > DEDUPE_SIZE) notifiedMessageIds.shift();
};

const wasNotified = messageId =>
  messageId !== undefined &&
  messageId !== null &&
  notifiedMessageIds.includes(String(messageId));

const truncate = (text, max = BODY_MAX_LENGTH) =>
  text.length > max ? `${text.slice(0, max - 1)}…` : text;

/**
 * Monta o corpo da notificação: "Fulano: mensagem" (ou "Fulano: Anexo").
 * @returns {string}
 */
export const buildInternalChatBody = (message = {}, attachmentLabel = '') => {
  const content = String(message.content || '').trim();
  const hasAttachment = Array.isArray(message.attachments)
    ? message.attachments.length > 0
    : false;
  const text = content || (hasAttachment ? attachmentLabel : '');
  const author = String(message.user_name || '').trim();
  if (!author) return truncate(text);
  return truncate(text ? `${author}: ${text}` : author);
};

const FALLBACK_LABELS = { title: 'Chat Interno', attachment: 'Anexo' };

// O notificador vive no componente RAIZ do dashboard; um erro aqui derrubaria a
// tela inteira. Por isso o i18n é opcional, com texto de reserva.
const useNotificationLabels = () => {
  try {
    const { t } = useI18n();
    return computed(() => ({
      title: t('CHATS.title'),
      attachment: t('CHATS.attachment'),
    }));
  } catch {
    return computed(() => FALLBACK_LABELS);
  }
};

/**
 * Notificador global. Chamado UMA vez no App.vue para que a notificação
 * funcione mesmo com o usuário em outra tela (tickets, kanban, relatórios).
 */
export function useInternalChatNotifier() {
  const router = useRouter();
  const labels = useNotificationLabels();
  const { accountId, currentAccount } = useAccount();
  const { checkPermissions } = usePolicy();
  const { uiSettings } = useUISettings();
  const currentUserId = useMapGetter('getCurrentUserID');
  const globalConfig = useMapGetter('globalConfig/get');

  // só notifica se o módulo estiver ligado pra conta E liberado pra função
  const moduleEnabled = computed(
    () =>
      currentAccount.value?.feature_modules?.internal_chat !== false &&
      checkPermissions([
        'administrator',
        'agent',
        KANBAN_MODULE_PERMISSION_BY_KEY.internal_chat,
      ])
  );
  // preferência do usuário; ausente = ligado (comportamento do sistema antigo)
  const prefEnabled = computed(
    () => uiSettings.value?.[INTERNAL_CHAT_NOTIFICATIONS_KEY] !== false
  );

  const openChat = chatId => {
    pendingChatId = chatId;
    // se a tela já estiver montada ela abre pelo evento; se não, abre no mount
    emitter.emit('kanban:internal_chat:open', chatId);
    const target = {
      name: 'kanban_internal_chats',
      params: { accountId: accountId.value },
    };
    Promise.resolve(router.push(target)).catch(() => {});
  };

  const notify = (chatId, chatName, message) => {
    try {
      const notification = new window.Notification(
        chatName || labels.value.title,
        {
          body: buildInternalChatBody(message, labels.value.attachment),
          icon: globalConfig.value?.logoThumbnail || undefined,
          // uma notificação por chat: mensagens novas substituem a anterior
          tag: `${NOTIFICATION_TAG_PREFIX}${chatId}`,
          // o som já é nosso (beep); deixar o SO tocar de novo vira eco
          silent: true,
        }
      );
      notification.onclick = () => {
        window.focus();
        openChat(chatId);
        notification.close();
      };
      setTimeout(() => {
        try {
          notification.close();
        } catch {
          // notificação já fechada pelo usuário
        }
      }, NOTIFICATION_AUTO_CLOSE_MS);
    } catch {
      // navegador pode recusar (ex.: exige Service Worker no Android) — o som
      // e o badge da tela continuam funcionando, então só ignoramos.
    }
  };

  const isSoundEnabled = () => {
    try {
      return localStorage.getItem(SOUND_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  };

  const handleMessage = data => {
    if (!data || !moduleEnabled.value) return;
    const { chat_id: chatId, chat_name: chatName, message } = data;
    if (!message) return;

    const common = {
      eventAccountId: data.account_id,
      currentAccountId: accountId.value,
      senderId: message.user_id,
      currentUserId: currentUserId.value,
      chatId,
    };

    if (
      shouldPlayInternalChatSound({
        ...common,
        soundEnabled: isSoundEnabled(),
        viewMounted: isInternalChatViewMounted(),
      })
    ) {
      playInternalChatChime();
    }

    const ok = shouldNotifyInternalChat({
      ...common,
      enabled: prefEnabled.value,
      permission: getNotificationPermission(),
      activeChatId,
      // aba escondida OU janela sem foco = usuário não está vendo o chat
      documentHidden: document.hidden || !document.hasFocus(),
      alreadyNotified: wasNotified(message.id),
    });
    if (!ok) return;

    rememberNotified(message.id);
    notify(chatId, chatName, message);
  };

  // o mesmo evento tem outro ouvinte (a tela do Chat Interno); se estourasse
  // aqui, o mitt não chamaria o próximo e a mensagem não apareceria na tela
  const onMessage = data => {
    try {
      handleMessage(data);
    } catch {
      // notificação é acessório: falha silenciosa
    }
  };

  onMounted(() => {
    bindAudioUnlock();
    emitter.on('kanban:internal_chat:message', onMessage);
  });
  onBeforeUnmount(() => {
    emitter.off('kanban:internal_chat:message', onMessage);
  });
}
